import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Pencil, Trash2, FileText, Video, FileImage, Presentation,
  GripVertical, Upload, Eye, EyeOff, ChevronDown, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { value: "onboarding", label: "Onboarding" },
  { value: "product", label: "Product / Project" },
  { value: "skills", label: "Sales Skills" },
  { value: "compliance", label: "Compliance" },
  { value: "ai_coaching", label: "AI Coaching" },
];

const ROLES = [
  "admin", "ceo", "hod_sales", "sales_head", "sales_manager",
  "sales_rep", "telecaller", "marketing_manager", "channel_partner",
];

const MODULE_TYPE_META = {
  text: { label: "Text", icon: FileText, color: "bg-slate-500" },
  video: { label: "Video", icon: Video, color: "bg-red-500" },
  pdf: { label: "PDF", icon: FileImage, color: "bg-blue-500" },
  slide: { label: "Slides", icon: Presentation, color: "bg-amber-500" },
};

const emptyCourse = () => ({
  title: "", description: "", category: "onboarding",
  audience_roles: [...ROLES], thumbnail_path: null, published: false,
});

const TrainingAdminPage = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingCourseIdForModule, setEditingCourseIdForModule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/training/courses?include_unpublished=true`);
      setCourses(r.data?.courses || []);
    } catch { toast.error("Failed to load courses"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(emptyCourse()); setEditOpen(true); };
  const openEdit = (c) => { setEditing({ ...c }); setEditOpen(true); };

  const saveCourse = async () => {
    if (!editing.title?.trim()) return toast.error("Title is required");
    try {
      if (editing.course_id) {
        await axios.patch(`${API}/training/courses/${editing.course_id}`, editing);
        toast.success("Course updated");
      } else {
        await axios.post(`${API}/training/courses`, editing);
        toast.success("Course created");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteCourse = async (course_id) => {
    if (!window.confirm("Delete this course and ALL its modules + learner progress? Cannot be undone.")) return;
    try {
      await axios.delete(`${API}/training/courses/${course_id}`);
      toast.success("Course deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const togglePublish = async (c) => {
    try {
      await axios.patch(`${API}/training/courses/${c.course_id}`, { ...c, published: !c.published });
      toast.success(c.published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };

  const addModule = (course_id) => {
    setEditingCourseIdForModule(course_id);
    setEditingModule({ title: "", type: "text", content_text: "", video_url: "", file_path: null, file_name: null, duration_mins: null });
    setModuleOpen(true);
  };
  const editModule = (course_id, mod) => {
    setEditingCourseIdForModule(course_id);
    setEditingModule({ ...mod });
    setModuleOpen(true);
  };

  const saveModule = async () => {
    if (!editingModule.title?.trim()) return toast.error("Title required");
    const m = editingModule;
    if (m.type === "text" && !m.content_text?.trim()) return toast.error("Content text required");
    if (m.type === "video" && !m.video_url?.trim()) return toast.error("Video URL required");
    if ((m.type === "pdf" || m.type === "slide") && !m.file_path) return toast.error("Upload a file first");
    try {
      if (m.module_id) {
        await axios.patch(`${API}/training/courses/${editingCourseIdForModule}/modules/${m.module_id}`, m);
        toast.success("Module updated");
      } else {
        await axios.post(`${API}/training/courses/${editingCourseIdForModule}/modules`, m);
        toast.success("Module added");
      }
      setModuleOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteModule = async (course_id, module_id) => {
    if (!window.confirm("Delete this module?")) return;
    try {
      await axios.delete(`${API}/training/courses/${course_id}/modules/${module_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const moveModule = async (c, idx, dir) => {
    const mods = [...(c.modules || [])];
    const to = idx + dir;
    if (to < 0 || to >= mods.length) return;
    [mods[idx], mods[to]] = [mods[to], mods[idx]];
    try {
      await axios.post(`${API}/training/courses/${c.course_id}/modules/reorder`, {
        module_ids: mods.map((x) => x.module_id),
      });
      load();
    } catch { toast.error("Reorder failed"); }
  };

  if (!["admin", "ceo", "hod_sales"].includes(user?.role)) {
    return <Layout><div className="p-6 text-slate-500">Admin only.</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="training-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-violet-600" /> Training Admin
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Build courses, add lesson modules, publish to the team.
            </p>
          </div>
          <Button onClick={openCreate} data-testid="new-course-btn" className="bg-violet-600 hover:bg-violet-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Course
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            No courses yet. Create your first one.
          </div>
        ) : (
          <div className="space-y-3">
            {courses.map((c) => {
              const open = expanded === c.course_id;
              return (
                <Card key={c.course_id} className="border-slate-200" data-testid={`course-card-${c.course_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <button className="flex-1 flex items-start gap-3 text-left"
                              onClick={() => setExpanded(open ? null : c.course_id)}>
                        {open ? <ChevronDown className="w-4 h-4 mt-0.5 text-slate-500" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-slate-500" />}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">{c.title}</p>
                            <Badge className="bg-slate-100 text-slate-700 text-[10px]">{c.category}</Badge>
                            <Badge className={c.published
                              ? "bg-emerald-100 text-emerald-700 text-[10px]"
                              : "bg-amber-100 text-amber-700 text-[10px]"}>
                              {c.published ? "Published" : "Draft"}
                            </Badge>
                            <span className="text-xs text-slate-500">{(c.modules || []).length} modules</span>
                          </div>
                          {c.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.description}</p>}
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => togglePublish(c)}
                                data-testid={`publish-toggle-${c.course_id}`}>
                          {c.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                                data-testid={`edit-course-${c.course_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                                onClick={() => deleteCourse(c.course_id)}
                                data-testid={`delete-course-${c.course_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {open && (
                      <div className="mt-4 pl-6 space-y-2" data-testid={`modules-${c.course_id}`}>
                        {(c.modules || []).length === 0 && (
                          <p className="text-xs text-slate-400">No modules yet.</p>
                        )}
                        {(c.modules || []).map((m, idx) => {
                          const meta = MODULE_TYPE_META[m.type] || MODULE_TYPE_META.text;
                          const Icon = meta.icon;
                          return (
                            <div key={m.module_id}
                                 className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200"
                                 data-testid={`module-row-${m.module_id}`}>
                              <GripVertical className="w-4 h-4 text-slate-400" />
                              <div className={`w-7 h-7 rounded ${meta.color} flex items-center justify-center`}>
                                <Icon className="w-3.5 h-3.5 text-white" />
                              </div>
                              <span className="text-xs text-slate-500 w-6">#{m.order}</span>
                              <span className="flex-1 text-sm text-slate-800">{m.title}</span>
                              <span className="text-[11px] text-slate-500">{meta.label}</span>
                              <Button size="sm" variant="ghost" className="h-7 px-2"
                                      onClick={() => moveModule(c, idx, -1)} disabled={idx === 0}>↑</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2"
                                      onClick={() => moveModule(c, idx, 1)}
                                      disabled={idx === (c.modules.length - 1)}>↓</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2"
                                      onClick={() => editModule(c.course_id, m)}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600"
                                      onClick={() => deleteModule(c.course_id, m.module_id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          );
                        })}
                        <Button size="sm" variant="outline" onClick={() => addModule(c.course_id)}
                                data-testid={`add-module-${c.course_id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add Module
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <CourseDialog open={editOpen} onOpenChange={setEditOpen} value={editing}
                      onChange={setEditing} onSave={saveCourse} />
        <ModuleDialog open={moduleOpen} onOpenChange={setModuleOpen} value={editingModule}
                      onChange={setEditingModule} onSave={saveModule} />
      </div>
    </Layout>
  );
};

const CourseDialog = ({ open, onOpenChange, value, onChange, onSave }) => {
  if (!value) return null;
  const toggleRole = (r) => {
    const has = value.audience_roles.includes(r);
    onChange({
      ...value,
      audience_roles: has ? value.audience_roles.filter((x) => x !== r) : [...value.audience_roles, r],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="course-dialog">
        <DialogHeader>
          <DialogTitle>{value.course_id ? "Edit Course" : "New Course"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Title</label>
            <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })}
                   data-testid="course-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Description</label>
            <Textarea value={value.description || ""} rows={3}
                      onChange={(e) => onChange({ ...value, description: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Category</label>
            <Select value={value.category} onValueChange={(v) => onChange({ ...value, category: v })}>
              <SelectTrigger data-testid="course-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Audience Roles</label>
            <div className="flex flex-wrap gap-1">
              {ROLES.map((r) => (
                <button key={r} type="button" onClick={() => toggleRole(r)}
                        className={`text-[11px] px-2 py-1 rounded-full border ${
                          value.audience_roles.includes(r)
                            ? "bg-violet-100 text-violet-700 border-violet-300"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        }`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm pt-2">
            <input type="checkbox" checked={!!value.published}
                   onChange={(e) => onChange({ ...value, published: e.target.checked })}
                   data-testid="course-published-checkbox" />
            Published (visible to learners)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} data-testid="course-save-btn"
                  className="bg-violet-600 hover:bg-violet-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ModuleDialog = ({ open, onOpenChange, value, onChange, onSave }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  if (!value) return null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const kind = value.type === "pdf" ? "pdf" : value.type === "slide" ? "slide" : "file";
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      const res = await axios.post(`${API}/training/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ ...value, file_path: res.data.path, file_name: res.data.filename });
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="module-dialog">
        <DialogHeader>
          <DialogTitle>{value.module_id ? "Edit Module" : "New Module"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Title</label>
            <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })}
                   data-testid="module-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Type</label>
            <Select value={value.type}
                    onValueChange={(v) => onChange({ ...value, type: v, file_path: null, file_name: null, content_text: "", video_url: "" })}>
              <SelectTrigger data-testid="module-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text lesson</SelectItem>
                <SelectItem value="video">Video (YouTube / Loom / Vimeo link)</SelectItem>
                <SelectItem value="pdf">PDF (upload)</SelectItem>
                <SelectItem value="slide">Slide deck (upload)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {value.type === "text" && (
            <div>
              <label className="text-xs font-medium text-slate-600">Content</label>
              <Textarea rows={10} value={value.content_text || ""}
                        onChange={(e) => onChange({ ...value, content_text: e.target.value })}
                        placeholder="Write your lesson. Plain text or basic markdown-style formatting."
                        data-testid="module-text-input" />
            </div>
          )}

          {value.type === "video" && (
            <div>
              <label className="text-xs font-medium text-slate-600">Video URL</label>
              <Input value={value.video_url || ""}
                     onChange={(e) => onChange({ ...value, video_url: e.target.value })}
                     placeholder="https://youtube.com/watch?v=... or https://loom.com/share/..."
                     data-testid="module-video-url" />
              <p className="text-[10px] text-slate-500 mt-1">YouTube, Loom, or Vimeo only.</p>
            </div>
          )}

          {(value.type === "pdf" || value.type === "slide") && (
            <div>
              <label className="text-xs font-medium text-slate-600">
                {value.type === "pdf" ? "PDF file" : "Slide deck (PPT/PPTX/PDF)"}
              </label>
              <input ref={fileInputRef} type="file"
                     accept={value.type === "pdf" ? "application/pdf" : ".ppt,.pptx,.pdf"}
                     className="hidden" onChange={handleUpload}
                     data-testid="module-file-input" />
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload file"}
                </Button>
                {value.file_name && (
                  <span className="text-xs text-slate-600 truncate">{value.file_name}</span>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600">Duration (minutes, optional)</label>
            <Input type="number" value={value.duration_mins ?? ""} min="0"
                   onChange={(e) => onChange({ ...value, duration_mins: e.target.value ? parseInt(e.target.value, 10) : null })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} data-testid="module-save-btn"
                  className="bg-violet-600 hover:bg-violet-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrainingAdminPage;
